import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Building2,
  Link2,
  MoreHorizontal,
  RotateCcw,
  Unlink,
  UserRound,
  XCircle,
} from 'lucide-react';
import {
  CONSOLE_STATE_STYLE,
  SYNC_HEALTH_STYLE,
  connectionOf,
  consoleStateLabel,
  controllerTypeLabel,
  isDirectoryType,
  siteStateLabel,
  syncHealthLabel,
  useController,
  useControllerDevices,
  useControllerSites,
  useDismissSite,
  useRestoreSite,
  useUnlinkSite,
  type Controller,
  type ControllerDevice,
  type DiscoveredSite,
  type ManagedThrough,
} from '../../hooks/useControllers';
import { usePlacements, type Placement } from '../../hooks/usePlacements';
import PlacementLabel from '../../components/PlacementLabel';
import { useConfirm } from '../../hooks/useConfirm';
import { AdoptSiteSheet, MatchSiteSheet } from '../../components/DiscoveredSiteSheets';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { deviceTypeLabel } from '@/types/deviceType';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { Router } from 'lucide-react';
import { cn } from '@/lib/utils';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm break-words">{value ?? '—'}</dd>
    </div>
  );
}

/** The device-status palette the equipment manager uses, so the same status
 *  reads the same everywhere. `unknown` is neutral grey on purpose: nobody is
 *  currently in a position to say whether the device is alive, which is not the
 *  same as a fault — and this page is where a controller outage shows up first. */
const STATUS_STYLE: Record<string, string> = {
  online: 'bg-sev-4/15 text-sev-4',
  offline: 'bg-sev-1/15 text-sev-1',
  unknown: 'bg-muted text-muted-foreground',
};

const DEVICE_COLUMNS: readonly ResponsiveTableColumn<ControllerDevice>[] = [
  {
    key: 'name',
    label: 'Name',
    hideMobileLabel: true,
    render: (d) => (
      <Link to={`/devices/${d.id}`} className="font-medium hover:underline">
        {d.name || d.ipAddress || d.macAddress || '[Unnamed]'}
      </Link>
    ),
  },
  {
    key: 'deviceType',
    label: 'Type',
    render: (d) => <span className="text-sm">{deviceTypeLabel(d.deviceType)}</span>,
  },
  {
    key: 'status',
    label: 'Status',
    render: (d) => (
      <Badge
        className={cn(
          'border-0 font-normal capitalize',
          STATUS_STYLE[d.status] ?? 'bg-muted text-muted-foreground',
        )}
      >
        {d.status}
      </Badge>
    ),
  },
  {
    key: 'ipAddress',
    label: 'IP',
    render: (d) => <span className="text-sm tabular-nums">{d.ipAddress ?? '—'}</span>,
  },
  {
    key: 'model',
    label: 'Model',
    render: (d) => <span className="text-sm">{d.model ?? '—'}</span>,
  },
];

function ago(iso: string): string {
  return `${formatDistanceToNowStrict(new Date(iso))} ago`;
}

function siteColumns(
  placementOf: (regionId: string) => Placement | undefined,
): readonly ResponsiveTableColumn<DiscoveredSite>[] {
  return [
    {
      key: 'console',
      label: 'Console',
      hideMobileLabel: true,
      render: (s) => (
        <div className="min-w-0">
          <p className="font-medium">{s.consoleName ?? s.externalConsoleId}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {[s.consoleModel, s.consoleAddress].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'site',
      label: 'Site',
      render: (s) => <span className="text-sm">{s.siteName ?? s.siteReference}</span>,
    },
    {
      key: 'consoleState',
      label: 'Status',
      render: (s) =>
        s.missingSince ? (
          <Badge className="border-0 font-normal bg-muted text-muted-foreground">
            Gone from account
          </Badge>
        ) : (
          <Badge
            className={cn(
              'border-0 font-normal',
              CONSOLE_STATE_STYLE[s.consoleState ?? ''] ?? 'bg-muted text-muted-foreground',
            )}
          >
            {consoleStateLabel(s.consoleState)}
          </Badge>
        ),
    },
    {
      key: 'belongsTo',
      label: 'Belongs to',
      render: (s) => {
        const placement = s.regionId ? placementOf(s.regionId) : undefined;
        return (
          <div className="min-w-0 text-sm">
            {placement ? (
              <p>
                <PlacementLabel placement={placement} />
              </p>
            ) : (
              <p>{siteStateLabel(s.state)}</p>
            )}
            {s.siteController ? (
              <Link
                to={`/controllers/${s.siteController.id}`}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-0.5"
              >
                <Link2 className="w-3 h-3" />
                {s.siteController.name} · {syncHealthLabel(s.siteController.syncHealth)}
              </Link>
            ) : placement && s.state !== 'dismissed' ? (
              <p className="text-xs text-muted-foreground mt-0.5">{siteStateLabel(s.state)}</p>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'lastSeen',
      label: 'Last seen',
      hideAt: 'mobile',
      render: (s) => (
        <span
          className="text-sm text-muted-foreground"
          title={new Date(s.lastSeenAt).toLocaleString()}
        >
          {ago(s.missingSince ?? s.lastSeenAt)}
        </span>
      ),
    },
  ];
}

/**
 * The likeliest reason a UniFi controller's key was refused: the wrong kind of
 * key for how it connects. The two kinds look identical and are both called
 * "API key" in UniFi, so say which one this connection needs.
 */
function keyMixupHint(controller: Controller): string | null {
  if (controller.type !== 'unifi' || controller.syncHealth !== 'auth_failed') return null;
  return connectionOf(controller.baseUrl) === 'cloud'
    ? "UniFi's cloud refused this key. It needs a key from Site Manager's API Keys page; one created on the console itself only works directly on the console's network."
    : "The console refused this key. It needs a key created on the console (Network → Settings → Control Plane → Integrations); a Site Manager key only works through UniFi's cloud.";
}

type SiteFilter = 'all' | 'new' | 'matched' | 'adopted' | 'missing' | 'dismissed';

const SITE_FILTERS: { value: SiteFilter; label: string; test: (s: DiscoveredSite) => boolean }[] = [
  { value: 'all', label: 'All', test: (s) => s.state !== 'dismissed' },
  // The three review filters match the Controllers list's "to review" counts
  // exactly: each site counts once, and one gone from the account is "gone"
  // whatever its state.
  { value: 'new', label: 'Not placed', test: (s) => s.state === 'new' && !s.missingSince },
  {
    value: 'matched',
    label: 'Ready to adopt',
    test: (s) => s.state === 'matched' && !s.missingSince,
  },
  { value: 'adopted', label: 'Adopted', test: (s) => s.state === 'adopted' },
  {
    value: 'missing',
    label: 'Gone from account',
    test: (s) => !!s.missingSince && s.state !== 'dismissed',
  },
  { value: 'dismissed', label: 'Dismissed', test: (s) => s.state === 'dismissed' },
];

function SitesSection({
  parent,
  sites,
}: {
  parent: Controller;
  sites: DiscoveredSite[] | undefined;
}) {
  const { placementOf } = usePlacements();
  const dismiss = useDismissSite(parent.id);
  const restore = useRestoreSite(parent.id);
  const unlink = useUnlinkSite(parent.id);
  const { confirm, confirmDialog } = useConfirm();

  const confirmUnlink = async (s: DiscoveredSite): Promise<void> => {
    const ok = await confirm({
      title: 'Unlink site controller',
      message: `Unlink "${s.siteController?.name ?? 'the site controller'}" from ${s.consoleName ?? s.siteReference}? The controller keeps running on its own; the site can be adopted again.`,
      confirmText: 'Unlink',
    });
    if (ok) unlink.mutate(s.id);
  };
  // The filter lives in `?sites=`, so the Controllers list's "to review" links
  // open the table already narrowed to what they counted.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('sites');
  const filter: SiteFilter = SITE_FILTERS.some((f) => f.value === requested)
    ? (requested as SiteFilter)
    : 'all';
  const setFilter = (next: SiteFilter): void => {
    setSearchParams(
      (params) => {
        if (next === 'all') params.delete('sites');
        else params.set('sites', next);
        return params;
      },
      { replace: true },
    );
  };
  const [adopting, setAdopting] = useState<DiscoveredSite | null>(null);
  const [matching, setMatching] = useState<DiscoveredSite | null>(null);

  const all = sites ?? [];
  const active = SITE_FILTERS.find((f) => f.value === filter) ?? SITE_FILTERS[0];
  const rows = all.filter(active.test);

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold">Sites</h2>
        {all.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {SITE_FILTERS.map((f) => {
              const count = all.filter(f.test).length;
              if (f.value !== 'all' && count === 0) return null;
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => {
                    setFilter(f.value);
                  }}
                  className={cn(
                    'rounded-full border px-2.5 py-0.5 text-xs',
                    filter === f.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted',
                  )}
                >
                  {f.label} <span className="tabular-nums">{count}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      {all.length === 0 ? (
        <EmptyState
          variant="no-data"
          icon={Building2}
          title="No sites discovered yet"
          description="Every console and site on this UniFi account appears here after the gateway reads it."
        />
      ) : (
        <ResponsiveTable<DiscoveredSite>
          columns={siteColumns(placementOf)}
          rows={rows}
          rowKey={(r) => r.id}
          rowActions={(s) =>
            s.state === 'adopted' ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`More actions for ${s.consoleName ?? s.siteReference}`}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={() => {
                      void confirmUnlink(s);
                    }}
                  >
                    <Unlink className="w-4 h-4" />
                    Unlink controller
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-1">
                {s.state !== 'dismissed' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setAdopting(s);
                    }}
                  >
                    Adopt
                  </Button>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`More actions for ${s.consoleName ?? s.siteReference}`}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {s.state === 'dismissed' ? (
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() => {
                          restore.mutate(s.id);
                        }}
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restore
                      </DropdownMenuItem>
                    ) : (
                      <>
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() => {
                            setMatching(s);
                          }}
                        >
                          <UserRound className="w-4 h-4" />
                          {s.regionId ? 'Move' : 'Choose where it belongs'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() => {
                            dismiss.mutate(s.id);
                          }}
                        >
                          <XCircle className="w-4 h-4" />
                          Dismiss — not ours to monitor
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )
          }
        />
      )}
      <AdoptSiteSheet
        parent={parent}
        site={adopting}
        onClose={() => {
          setAdopting(null);
        }}
      />
      <MatchSiteSheet
        parent={parent}
        site={matching}
        onClose={() => {
          setMatching(null);
        }}
      />
      {confirmDialog}
    </div>
  );
}

/** "Managed through Site Manager", with the console's cloud status and Unlink. */
function ManagedThroughBanner({
  controller,
  managedThrough,
}: {
  controller: Controller;
  managedThrough: ManagedThrough;
}) {
  const unlink = useUnlinkSite(managedThrough.parentId);
  const { confirm, confirmDialog } = useConfirm();

  const onUnlink = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Unlink from Site Manager',
      message: `Unlink "${controller.name}" from ${managedThrough.parentName}? It keeps running on its own, and the site can be adopted again.`,
      confirmText: 'Unlink',
    });
    if (ok) unlink.mutate(managedThrough.discoveredSiteId);
  };

  return (
    <div className="mb-5 rounded-lg border p-3 text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-muted-foreground">Managed through Site Manager:</span>
      <Link to={`/controllers/${managedThrough.parentId}`} className="font-medium hover:underline">
        {managedThrough.parentName}
      </Link>
      {managedThrough.missingSince ? (
        <Badge className="border-0 font-normal bg-muted text-muted-foreground">
          Gone from account
        </Badge>
      ) : (
        <Badge
          className={cn(
            'border-0 font-normal',
            CONSOLE_STATE_STYLE[managedThrough.consoleState ?? ''] ??
              'bg-muted text-muted-foreground',
          )}
        >
          Console {consoleStateLabel(managedThrough.consoleState).toLowerCase()}
        </Badge>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="ml-auto gap-1.5"
        disabled={unlink.isPending}
        onClick={() => {
          void onUnlink();
        }}
      >
        <Unlink className="w-4 h-4" />
        Unlink
      </Button>
      {confirmDialog}
    </div>
  );
}

export default function ControllerDetails() {
  const { id } = useParams<{ id: string }>();
  const { data: controller, isLoading, isError } = useController(id);
  const isDirectory = controller ? isDirectoryType(controller.type) : false;
  const { data: devices } = useControllerDevices(controller && !isDirectory ? id : undefined);
  const { data: sites } = useControllerSites(id, isDirectory);

  if (isLoading) {
    return (
      <PageContainer>
        <p className="text-sm text-muted-foreground">Loading controller…</p>
      </PageContainer>
    );
  }
  if (isError || !controller) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load this controller.</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const lastSync = controller.lastSyncAt
    ? new Date(controller.lastSyncAt).toLocaleString()
    : 'Never';

  return (
    <PageContainer>
      <Link
        to="/controllers"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
      >
        <ArrowLeft className="w-4 h-4" />
        Controllers
      </Link>

      <PageHeader
        title={controller.name}
        subtitle={`${controllerTypeLabel(controller.type)} · ${controller.baseUrl}`}
        primary={
          <Badge
            className={cn(
              'border-0 font-normal',
              SYNC_HEALTH_STYLE[controller.syncHealth] ?? 'bg-muted text-muted-foreground',
            )}
          >
            {syncHealthLabel(controller.syncHealth)}
          </Badge>
        }
      />

      {controller.syncHealth !== 'ok' && controller.lastError ? (
        <Alert variant="destructive" className="mb-5">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {controller.lastError}
            {keyMixupHint(controller) ? <p className="mt-1">{keyMixupHint(controller)}</p> : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {controller.inCustomerRegion ? (
        <Alert className="mb-5 border-sev-2/40">
          <AlertTriangle className="h-4 w-4 text-sev-2" />
          <AlertDescription>
            This Site Manager key sits in {controller.inCustomerRegion}&apos;s region, so it is
            copied to {controller.inCustomerRegion}&apos;s gateways, and it can read every
            customer&apos;s console. Move it to a region of your own.
          </AlertDescription>
        </Alert>
      ) : null}

      {controller.managedThrough ? (
        <ManagedThroughBanner controller={controller} managedThrough={controller.managedThrough} />
      ) : null}

      <div className="grid gap-5 sm:grid-cols-3 mb-6">
        {isDirectory ? (
          <>
            <div className="rounded-lg border p-4">
              <p className="text-xs text-muted-foreground">Sites discovered</p>
              <p className="text-2xl font-semibold tabular-nums">{controller.siteCount ?? 0}</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs text-muted-foreground">Consoles offline</p>
              <p className="text-2xl font-semibold tabular-nums">
                {
                  new Set(
                    (sites ?? [])
                      .filter((s) => !s.missingSince && s.consoleState !== 'connected')
                      .map((s) => s.externalConsoleId),
                  ).size
                }
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg border p-4">
              <p className="text-xs text-muted-foreground">Devices imported</p>
              <p className="text-2xl font-semibold tabular-nums">{controller.deviceCount}</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs text-muted-foreground">Clients seen</p>
              <p className="text-2xl font-semibold tabular-nums">{controller.clientCount}</p>
            </div>
          </>
        )}
        <div className="rounded-lg border p-4">
          <p className="text-xs text-muted-foreground">Last sync</p>
          <p className="text-sm font-medium mt-1.5">{lastSync}</p>
        </div>
      </div>

      <div className="rounded-lg border p-5">
        <h2 className="text-sm font-semibold mb-4">Configuration</h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <Field label="Type" value={controllerTypeLabel(controller.type)} />
          <Field label={isDirectory ? 'API' : 'Controller URL'} value={controller.baseUrl} />
          {isDirectory ? null : (
            <>
              <Field label="Site" value={controller.site || 'default'} />
              <Field label="Verify TLS" value={controller.verifyTls ? 'Yes' : 'No'} />
            </>
          )}
          <Field label="Enabled" value={controller.enabled ? 'Yes' : 'No'} />
          <Field
            label="Sync interval"
            value={
              controller.syncIntervalSecs ? `${String(controller.syncIntervalSecs)}s` : 'Default'
            }
          />
          {controller.description ? (
            <Field label="Description" value={controller.description} />
          ) : null}
        </dl>
      </div>

      {isDirectory ? (
        <SitesSection parent={controller} sites={sites} />
      ) : (
        <div className="mt-6">
          <h2 className="text-sm font-semibold mb-3">Imported devices</h2>
          {!devices || devices.length === 0 ? (
            <EmptyState
              variant="no-data"
              icon={Router}
              title="No devices imported yet"
              description="Devices appear here after the gateway syncs this controller."
            />
          ) : (
            <ResponsiveTable<ControllerDevice>
              columns={DEVICE_COLUMNS}
              rows={devices}
              rowKey={(r) => r.id}
            />
          )}
        </div>
      )}
    </PageContainer>
  );
}
